import {
	BVPixelResponse,
	ProductReviewDrawerType,
	ProductReviewed,
	Review,
	openProductReviewDrawer,
	selectBVPixelisAvailable,
	setReviewOnDrawer
} from '@product-reviews/data-access';
import { BVProduct, BVTrackImpresionObject, RequiredSEOFields } from '../models';
/** @format */
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	OnInit,
	PLATFORM_ID,
	computed,
	inject,
	input,
	signal
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconMoneyGiftcard,
	homefieldIconSignCircledCheckOutline,
	homefieldIconStarFilled,
	homefieldIconStarHalf,
	homefieldIconStarOutlined
} from '@dsg-tech/homefield-angular-icons';

import { DateAgoPipe } from '../pipes/date-ago.pipe';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { ReviewBadgesComponent } from '../review-badges/review-badges.component';
import { ReviewStarsComponent } from '../review-stars/review-stars.component';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'product-reviews-review-card',
	templateUrl: './review-card.component.html',
	styleUrl: './review-card.component.scss',
	imports: [
		CommonModule,
		HmfActionTextComponent,
		HomefieldIconLibraryModule,
		DateAgoPipe,
		ReviewBadgesComponent,
		ReviewStarsComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewCardComponent implements OnInit {
	readonly review =  input<Review>();
	readonly product =  input<ProductReviewed>();

	private readonly store = inject(Store);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
	private readonly destroyRef = inject(DestroyRef);

	readonly starsOptions = signal({
		width: '16px',
		height: '16px'
	});

	readonly requiredSEOFields = signal(Object.values(RequiredSEOFields));

	readonly bvPixelResponse = signal<BVPixelResponse>(null);

	readonly hasRequiredValuesToSEO = computed(() => {
		const review = this.review();
		if (!review) return false;

		return !this.requiredSEOFields().some((field) => {
			const value = review[field as keyof Review];
			return value === null || value === '';
		});
	});

	selectBVData$ = this.store.select(selectBVPixelisAvailable);

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconStarFilled,
			homefieldIconStarHalf,
			homefieldIconStarOutlined,
			homefieldIconSignCircledCheckOutline,
			homefieldIconMoneyGiftcard
		]);
	}

	ngOnInit() {
		if (isPlatformBrowser(this.platformId)) {
			this.selectBVData$
				.pipe(
					takeUntilDestroyed(this.destroyRef),
					filter((resp: BVPixelResponse) => !!resp && resp.isBVPixelReady)
				)
				.subscribe((bv: BVPixelResponse) => {
					this.bvPixelResponse.set(bv);
					this.handleBVPixel();
				});
		}
	}

	openDrawer(review: string) {
		this.store.dispatch(
			setReviewOnDrawer({
				currentReview: review // TODO: verify ID of the review to pass to drawer, the open it
			})
		);

		const contentListDrawer = { drawerType: ProductReviewDrawerType.REVIEWS };
		this.store.dispatch(openProductReviewDrawer(contentListDrawer));
	}

	private handleBVPixel(): void {
		if (!!BV && BV.pixel) {
			const reviewImpressionData: BVTrackImpresionObject = {
				brand: this.bvPixelResponse().brand,
				bvProduct: BVProduct.RATINGSANDREVIEWS,
				contentId: this.review().Id,
				categoryId: this.bvPixelResponse().category,
				contentType: 'review',
				productId: this.bvPixelResponse().productId
			};

			BV.pixel.trackImpression(reviewImpressionData);
		}
	}
}
